import type { MandobEarnings, MandobPayout, MandobProfile } from '@erp/shared';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/shadcn/index.js';
import { Button, FormField, Input, Skeleton } from '../../components/ui/index.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatDate } from '../../lib/intl.js';
import { getMandobEarnings, listMandobPayouts, runMandobPayout } from '../../lib/mandob.js';
import { formatDecimals } from '../../lib/money.js';

/**
 * Pay a rep: salary plus whatever commission has accrued, as one balanced entry.
 *
 * Lives in its own file because it is opened from two places — the roster and
 * the rep's own profile. It was previously private to the roster page, which is
 * why paying somebody meant going back out to the list.
 *
 * `onDone` fires after a successful payout so the page underneath can refetch:
 * the payout changes the rep's accrued commission and their payout history, and
 * both are on screen behind this dialog.
 */
export function PayoutDialog({ mandob, onClose, onDone }: {
  mandob: Pick<MandobProfile, 'id' | 'name'>;
  onClose: () => void;
  onDone?: () => void | Promise<void>;
}): JSX.Element {
  const { t, i18n } = useTranslation('mandob');
  const { t: tc } = useTranslation();
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';
  const num = (n: string | number): string => formatDecimals(n, baseCurrency, i18n.language);
  const [earnings, setEarnings] = useState<MandobEarnings | null>(null);
  const [history, setHistory] = useState<MandobPayout[]>([]);
  const [salary, setSalary] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const [e, h] = await Promise.all([getMandobEarnings(mandob.id), listMandobPayouts(mandob.id)]);
      setEarnings(e); setHistory(h); setSalary(e.salary);
    } catch { toast.error(tc('errors:server')); }
  };
  useEffect(() => { void load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [mandob.id]);

  const accrued = Number(earnings?.commissionAccrued ?? 0);
  const total = Number(salary || 0) + accrued;

  const submit = async (): Promise<void> => {
    setBusy(true);
    try {
      const p = await runMandobPayout(mandob.id, {
        salaryAmount: salary || '0',
        periodStart: periodStart || undefined,
        periodEnd: periodEnd || undefined,
        note: note.trim() || undefined,
      });
      toast.success(t('payout.done', { defaultValue: 'Paid {{amount}}', amount: num(p.total) }));
      setNote('');
      await load();
      await onDone?.();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'unprocessable' ? t('payout.nothing', { defaultValue: 'Nothing to pay.' }) : tc('errors:server'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{t('payout.title', { defaultValue: 'Pay {{name}}', name: mandob.name })}</DialogTitle></DialogHeader>
        {!earnings ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="flex flex-col gap-3 py-2">
            <div className="grid grid-cols-3 gap-2 rounded-lg border border-border p-3 text-sm">
              <div><div className="text-xs text-fg-muted">{t('columns.salary', { defaultValue: 'Salary' })}</div><div className="font-mono">{num(salary || '0')}</div></div>
              <div><div className="text-xs text-fg-muted">{t('payout.commission', { defaultValue: 'Commission' })}</div><div className="font-mono">{num(accrued)}</div></div>
              <div><div className="text-xs text-fg-muted">{t('payout.total', { defaultValue: 'Total' })}</div><div className="font-mono font-semibold text-fg">{num(total)}</div></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField id="po-salary" label={t('columns.salary', { defaultValue: 'Salary' })}>
                <Input id="po-salary" inputMode="decimal" value={salary} onChange={(e) => setSalary(e.target.value)} />
              </FormField>
              <FormField id="po-note" label={tc('columns.notes', { defaultValue: 'Notes' })}>
                <Input id="po-note" value={note} onChange={(e) => setNote(e.target.value)} />
              </FormField>
              <FormField id="po-start" label={t('payout.periodStart', { defaultValue: 'Period start' })}>
                <Input id="po-start" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
              </FormField>
              <FormField id="po-end" label={t('payout.periodEnd', { defaultValue: 'Period end' })}>
                <Input id="po-end" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
              </FormField>
            </div>
            {history.length > 0 && (
              <div>
                <div className="mb-1 text-xs font-semibold text-fg-muted">{t('payout.history', { defaultValue: 'Recent payouts' })}</div>
                <ul className="divide-y divide-border text-sm">
                  {history.slice(0, 5).map((p) => (
                    <li key={p.id} className="flex items-center justify-between py-1.5">
                      <span className="text-fg-muted">{formatDate(p.createdAt, { locale: i18n.language })}{p.note ? ` · ${p.note}` : ''}</span>
                      <span className="font-mono">{num(p.total)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>{tc('actions.close', { defaultValue: 'Close' })}</Button>
          <Button onClick={submit} loading={busy} disabled={!earnings || total <= 0}>{t('payout.run', { defaultValue: 'Run payout' })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
